/* eslint-disable */

import { useEffect, useMemo, useState } from 'react'
import {
    DataGridPro,
    type GridColDef,
    type GridRenderCellParams,
    type GridRowSelectionModel,
} from '@mui/x-data-grid-pro'
import { useTranslation } from 'react-i18next'
import { gettingInterpretationRule } from 'utils/InterpretationPunchRule'
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import InfoIcon from '@mui/icons-material/Info'
import CachedIcon from '@mui/icons-material/Cached'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { AddInterpretationRule } from 'components/dialog/interpretation_rules/AddInterpretatioRule'
import { EditInterpretationRule } from 'components/dialog/interpretation_rules/EditInterpretationRule'
import { InfoInterpretationRule } from 'components/dialog/interpretation_rules/InfoInterpretationRule'
import { DeleteInterpretationRule } from 'components/dialog/interpretation_rules/DeleteInterpretationRule'
import { useLocation, useNavigate } from 'react-router-dom'
import { buildSettingsBasePath, buildSettingsCrudPath, parseSettingsCrudRoute } from 'utils/settingsCrudRouting'

interface InterpretationRuleRow {
    id: number
    name: string
    punch_restriction?: string | null
    allow_substitution?: boolean | null
    enable?: boolean | null
}

interface ContextMenuPosition {
    mouseX: number
    mouseY: number
}

interface ContextMenuProps {
    contextMenu: ContextMenuPosition | null
    setContextMenu: React.Dispatch<React.SetStateAction<ContextMenuPosition | null>>
    selectedRow: InterpretationRuleRow[]
    reloading: () => void
    rows: InterpretationRuleRow[]
    cleanSelected: () => void
}

interface MenuOption {
    name: string
    icon: React.ReactNode
    click: () => void
    color?: 'error' | ''
    disabled?: boolean
    permission: boolean
}

const getPermissions = (): string[] => {
    try {
        const rawPermissions = localStorage.getItem('permissions')
        if (!rawPermissions) {
            return []
        }

        const parsed = JSON.parse(rawPermissions)
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

const hasPermission = (permissions: string[], permissionName: string): boolean => {
    return permissions.some((permission) => permission === permissionName)
}

const ContextMenu = ({ contextMenu, setContextMenu, selectedRow, reloading, rows, cleanSelected }: ContextMenuProps) => {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const [openDelete, setOpenDelete] = useState(false)
    const basePath = buildSettingsBasePath('interpretation_punch_rule')

    const modalRoute = useMemo(() => parseSettingsCrudRoute(location.pathname, 'interpretation_punch_rule'), [location.pathname])
    const openAdd = modalRoute.action === 'add'
    const openEdit = modalRoute.action === 'update' && modalRoute.id !== null
    const openInfo = modalRoute.action === 'detail' && modalRoute.id !== null

    const selectedRowFromRoute = useMemo(() => {
        if (modalRoute.id === null) return null
        return rows.find((row) => Number(row.id) === modalRoute.id) || null
    }, [rows, modalRoute.id])
    const selectedRowsForRoute = selectedRowFromRoute ? [selectedRowFromRoute] : selectedRow

    const closeRouteModal = () => {
        navigate(basePath)
        cleanSelected()
    }

    const permissions = useMemo(() => getPermissions(), [])

    const options: MenuOption[] = [
        {
            name: t('settings.interpretation_rule.table.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildSettingsCrudPath('interpretation_punch_rule', 'add'))
                setContextMenu(null)
            },
            color: '',
            permission: hasPermission(permissions, 'timekeeper.CreateInterpretationRule'),
        },
        {
            name: t('settings.interpretation_rule.table.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                const current = selectedRow[0]
                if (!current?.id) return
                navigate(buildSettingsCrudPath('interpretation_punch_rule', 'update', current.id))
                setContextMenu(null)
            },
            color: '',
            disabled: selectedRow.length !== 1,
            permission: hasPermission(permissions, 'timekeeper.UpdateInterpretationRule'),
        },
        {
            name: t('settings.interpretation_rule.table.context_menu.delete'),
            icon: <DeleteIcon color='error' />,
            click: () => {
                setOpenDelete(true)
                setContextMenu(null)
            },
            color: 'error',
            disabled: true,
            permission: hasPermission(permissions, 'timekeeper.DeleteInterpretationRule'),
        },
        {
            name: t('settings.interpretation_rule.table.context_menu.info'),
            icon: <InfoIcon />,
            disabled: selectedRow.length !== 1,
            click: () => {
                const current = selectedRow[0]
                if (!current?.id) return
                navigate(buildSettingsCrudPath('interpretation_punch_rule', 'detail', current.id))
                setContextMenu(null)
            },
            color: '',
            permission: hasPermission(permissions, 'timekeeper.InterpretationRuleById'),
        },
        {
            name: t('settings.interpretation_rule.table.context_menu.reload'),
            icon: <CachedIcon />,
            color: '',
            click: () => {
                reloading()
                setContextMenu(null)
            },
            permission: true,
        },
    ]

    return (
        <>
            <Menu
                open={Boolean(contextMenu)}
                onClose={() => setContextMenu(null)}
                anchorReference='anchorPosition'
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                sx={{ boxShadow: 'none' }}
            >
                {options.filter((option) => option.permission).map((option, index) => (
                    <MenuItem key={index} onClick={option.click} disabled={option.disabled}>
                        <ListItemIcon>{option.icon}</ListItemIcon>
                        <ListItemText primary={option.name} slotProps={{ primary: { color: option.color } }} />
                    </MenuItem>
                ))}
            </Menu>

            <AddInterpretationRule open={openAdd} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal() }} rows={rows} reloading={reloading} />
            <EditInterpretationRule open={openEdit} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal() }} rows={rows} reloading={reloading} selectedRow={selectedRowsForRoute} cleanSelected={cleanSelected} />
            <InfoInterpretationRule open={openInfo} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal() }} selectedRow={selectedRowsForRoute} cleanSelected={cleanSelected} />
            <DeleteInterpretationRule open={openDelete} setOpenDelete={setOpenDelete} selectedRow={selectedRow} reloading={reloading} />
        </>
    )
}

const InterpretationPunchRuleTable = () => {
    const { t } = useTranslation()

    const [loading, setLoading] = useState(true)
    const [rows, setRows] = useState<InterpretationRuleRow[]>([])
    const [contextMenu, setContextMenu] = useState<ContextMenuPosition | null>(null)
    const [selectedRow, setSelectedRow] = useState<GridRowSelectionModel>([])
    const [selected, setSelected] = useState<InterpretationRuleRow[]>([])

    const columns: GridColDef<InterpretationRuleRow>[] = [
        {
            field: 'name',
            headerName: t('settings.interpretation_rule.table.columns.name'),
            flex: 1,
        },
        {
            field: 'punch_restriction',
            headerName: t('settings.interpretation_rule.table.columns.punch_restriction'),
            flex: 1,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: GridRenderCellParams<InterpretationRuleRow, string | null>) => (
                params.value === 'complete'
                    ? t('settings.interpretation_rule.dialog.inputs.complete')
                    : t('settings.interpretation_rule.dialog.inputs.simple')
            ),
        },
        {
            field: 'allow_substitution',
            headerName: t('settings.interpretation_rule.table.columns.allow_substitution'),
            flex: 1,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: GridRenderCellParams<InterpretationRuleRow, boolean | null>) => (
                params.value === true
                    ? <CheckIcon style={{ marginTop: '10px', marginBottom: '10px' }} />
                    : <CloseIcon style={{ marginTop: '10px', marginBottom: '10px' }} />
            ),
        },
        {
            field: 'enable',
            headerName: t('settings.interpretation_rule.table.columns.enable'),
            flex: 1,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: GridRenderCellParams<InterpretationRuleRow, boolean | null>) => (
                params.value === true
                    ? <CheckIcon style={{ marginTop: '10px', marginBottom: '10px' }} />
                    : <CloseIcon style={{ marginTop: '10px', marginBottom: '10px' }} />
            ),
        },
    ]

    const reloading = () => {
        const accessToken = localStorage.getItem('access_token')
        const permissions = getPermissions()

        if (accessToken && hasPermission(permissions, 'timekeeper.InterpretationRule')) {
            gettingInterpretationRule(setLoading, setRows)
        } else {
            setRows([])
            setLoading(false)
        }
    }

    const cleanSelected = () => {
        setSelectedRow([])
        setSelected([])
    }

    useEffect(() => {
        reloading()
    }, [])

    return (
        <div
            style={{ width: '100%', height: '100%' }}
            onContextMenu={(event) => {
                event.preventDefault()
                setContextMenu(
                    contextMenu === null
                        ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 }
                        : null,
                )
            }}
        >
            <DataGridPro
                sx={{ height: 'calc(100vh - 140px)' }}
                columns={columns}
                rows={rows}
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'name', sort: 'asc' }],
                    },
                }}
                showCellVerticalBorder
                showColumnVerticalBorder
                headerFilters
                localeText={{
                    headerFilterOperatorContains: t('general.table.headerFilters.headerFilterOperatorContains'),
                    headerFilterOperatorEquals: t('general.table.headerFilters.headerFilterOperatorEquals'),
                    headerFilterOperatorStartsWith: t('general.table.headerFilters.headerFilterOperatorStartsWith'),
                    headerFilterOperatorEndsWith: t('general.table.headerFilters.headerFilterOperatorEndsWith'),
                    headerFilterOperatorIsEmpty: t('general.table.headerFilters.headerFilterOperatorIsEmpty'),
                    headerFilterOperatorIsNotEmpty: t('general.table.headerFilters.headerFilterOperatorIsNotEmpty'),
                    footerRowSelected: (count) =>
                        count > 1
                            ? t('general.table.footerFilters.footerRowSelected.>1', { count: count.toLocaleString() })
                            : t('general.table.footerFilters.footerRowSelected.=1', { count: count.toLocaleString() }),
                    footerTotalRows: t('general.table.footerFilters.footerTotalRows'),
                    MuiTablePagination: {
                        labelDisplayedRows: ({ from, to, count }) =>
                            t('general.table.footerFilters.labelDisplayedRows', {
                                from: from.toLocaleString(),
                                to: to.toLocaleString(),
                                count: count.toLocaleString(),
                            }),
                        labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),
                    },
                }}
                disableColumnMenu
                loading={loading}
                checkboxSelection
                onRowSelectionModelChange={(newRowSelectionModel) => {
                    const selectedIds = new Set(newRowSelectionModel)
                    const selectedRows = rows.filter((row) => selectedIds.has(row.id))
                    setSelectedRow(newRowSelectionModel)
                    setSelected(selectedRows)
                }}
                rowSelectionModel={selectedRow}
                pagination
            />

            <ContextMenu
                contextMenu={contextMenu}
                setContextMenu={setContextMenu}
                selectedRow={selected}
                cleanSelected={cleanSelected}
                reloading={reloading}
                rows={rows}
            />

        </div>
    )
}

export { InterpretationPunchRuleTable }
